import { useMemo } from 'react';
import type { ConnectionDragState } from '../types';
import { dropTargetToEndpoint, generateSegmentId } from '../types';
import type { ConnectorData } from '../entityTypes';
import { useCanvasStores } from '../stores/CanvasContext';
import { useDragEffect, type DragEffectConfig } from './useDragEffect';

/**
 * Effect-only hook: registers global pointermove/pointerup/keydown listeners
 * for connection drag. Reads connectionDragRef from context (set by useCanvasConnectorOps).
 */
export function useConnectionDragEffect(): void {
  const { dragStore, store, connectionDragRef } = useCanvasStores();

  const config = useMemo<DragEffectConfig<ConnectionDragState>>(() => ({
    dragRef: connectionDragRef,
    isActive: () => true,
    getExcludeEntityId: (drag) => drag.fromEndpoint.entityId,
    onMove: (canvasPoint, drag, target) => {
      const updated: ConnectionDragState = { ...drag, currentPoint: canvasPoint };
      connectionDragRef.current = updated;
      dragStore.send({ type: 'connectionDrag.set', drag: updated, dropTarget: target });
    },
    onDrop: (_canvasPoint, drag, target) => {
      if (target) {
        store.send({
          type: 'connector.add',
          entity: {
            id: generateSegmentId(),
            kind: 'connector',
            x: 0, y: 0, width: 0, height: 0,
            layerId: '',
            connectable: false,
            zIndex: 0,
            data: {
              from: drag.fromEndpoint,
              to: dropTargetToEndpoint(target),
              segmentType: 'bezier',
            } satisfies ConnectorData,
          },
        });
      }
    },
    onCleanup: () => {
      connectionDragRef.current = null;
      dragStore.send({ type: 'connectionDrag.clear' });
    },
  }), [connectionDragRef, dragStore, store]);

  useDragEffect(config);
}
